// Single source of truth for notification category presentation — consumed by
// BOTH the notification center (NotificationCenter.tsx) and the live toaster
// (IbmNotificationToaster.tsx), which previously kept parallel TITLE/TONE/ROUTE
// and TOAST/SUPPRESS_ON maps that could drift for the same category. Pure data,
// no JSX, so it's importable anywhere and unit-testable.
//
// - tone   → row visual + section (action ⇒ NEEDS YOU + badge).
// - route  → deep-link target on click; ALSO the surface the toast is suppressed
//            on (the live inbox is the better surface) — one value, since for
//            every action category the two were identical.
// - family → glyph + filter family. NOTE the center treats "message" as booking
//            (family !== "team"), matching the retired `startsWith("appointment")`.
// - toast  → present ONLY on action categories (the high-signal arrivals that get
//            a live toast); its title/body are the toast copy (distinct from the
//            center's row `title`). Absence ⇒ center-only, no toast.
export type Tone = "action" | "booked" | "dim" | "rate"
export type Family = "booking" | "message" | "team"

export interface CategoryDescriptor {
    title: string
    tone: Tone
    route: string
    family: Family
    toast?: { title: string; body: string }
}

export const CATEGORIES: Record<string, CategoryDescriptor> = {
    "appointment-inquiry": {
        title: "New appointment request",
        tone: "action",
        route: "/appointment-requests",
        family: "booking",
        toast: { title: "New appointment request", body: "A patient is requesting care" },
    },
    "appointment-offer": {
        title: "A provider offered a time",
        tone: "action",
        route: "/ava",
        family: "booking",
        toast: { title: "A provider offered a time", body: "Review and accept in Ava" },
    },
    "appointment-recommendation": {
        // Named for what it means to the patient. Every origin (a practice raised
        // their hand, staff went looking, a sweep, a retry) converges here and
        // renders identically — no origin travels to the patient at all, in the
        // title, the body, or the conversation copy (BI §6).
        title: "Someone new to consider",
        // action — and therefore badged — because a recommendation nobody notices is a
        // wasted piece of staff outreach. Defensible ONLY because the server digests it
        // to one notification per search per batch and settles by ref on drain; one
        // ping per provider would have to be "dim".
        tone: "action",
        route: "/ava",
        family: "booking",
        toast: { title: "Someone new to consider", body: "Someone new is waiting in Ava" },
    },
    // --- Demand brokerage, provider side -----------------------------------
    //
    // Four categories rather than one "demand update", because the tone differs
    // and tone is what decides whether an item badges. Two need an answer FROM
    // the practice; two are status. A single category would have to pick one and
    // be wrong half the time — and badging status is how a badge gets ignored.
    "appointment-demand-invitation": {
        title: "Practice MD sent you a patient",
        tone: "action",
        route: "/appointment-requests",
        family: "booking",
        toast: { title: "Practice MD sent you a patient", body: "Someone is looking for care near you" },
    },
    "appointment-demand-revise": {
        // Not "rejected". The submission can still be cured, the staff message on
        // their board says how, and calling it a rejection would stop them reading
        // the one thing that tells them what to do.
        title: "Your response needs a small change",
        tone: "action",
        route: "/appointment-requests",
        family: "booking",
        toast: { title: "Your response needs a small change", body: "Practice MD left you a note" },
    },
    "appointment-demand-presented": {
        title: "You were presented to a patient",
        tone: "dim",
        route: "/appointment-requests",
        family: "booking",
    },
    "appointment-demand-closed": {
        title: "A Practice MD request closed",
        tone: "dim",
        route: "/appointment-requests",
        family: "booking",
    },
    // Staff side of the same loop: a practice expressed interest via an
    // outreach link or their board. Action — staff owe every interest a review,
    // and each hour it waits spends that practice's goodwill (the server backs
    // it with a 1h content-free email to staff-team members for the same
    // reason). Addressed to the designated staff team; settles by ref when the
    // review decides either way. Named for its referent row
    // (appointment_search_interest), not the appointment-demand-* prefix its
    // provider-side cousins carry.
    "appointment-search-interest": {
        title: "A practice expressed interest",
        tone: "action",
        route: "/settings/appointment-demand",
        family: "booking",
        toast: { title: "A practice expressed interest", body: "Review it in the demand console" },
    },
    // The two staff-side demand alerts. A search that matched nobody is the
    // moment brokering becomes possible, so it is the moment staff have to
    // hear; unbrokered is the second nudge for one already announced and not
    // dealt with. Both route to the console row. Named for their referent row
    // (appointment_searches) like the interest item above, not with the
    // appointment-demand-* prefix its provider-side cousins carry.
    "appointment-search-unmatched": {
        title: "A search matched nobody",
        tone: "action",
        route: "/settings/appointment-demand",
        family: "booking",
        toast: { title: "A search matched nobody", body: "Open the demand console" },
    },
    "appointment-search-unbrokered": {
        title: "Demand is going unanswered",
        tone: "action",
        route: "/settings/appointment-demand",
        family: "booking",
        toast: { title: "Demand is going unanswered", body: "Open the demand console" },
    },

    // A clinician asked to join through the public /start form. Owed a follow-up
    // by a person, which is what that page promises in as many words. The last
    // inbound growth event to get a notification, and last for a concrete
    // reason: until the onboarding queue existed there was nowhere for one to
    // point (practicemd-api docs/Growth-Event-Alerting.md, O4).
    "onboarding-request": {
        title: "A clinician asked to join",
        tone: "action",
        route: "/settings/onboarding",
        family: "team",
        toast: { title: "A clinician asked to join", body: "Open the onboarding queue" },
    },

    // Somebody created an account (owner 2026-09-27). Staff admins only: the
    // server addresses it to each of them as a person, never to the team.
    // "dim" because nothing is owed, so no badge and no toast. Its push carries
    // the new account's email as the notification body, resolved at send time
    // (practicemd-api docs/Growth-Event-Alerting.md §13); the bell row shows
    // only this title, and the page it opens shows who.
    "account-created": {
        title: "New account",
        tone: "dim",
        route: "/settings/accounts",
        family: "team",
    },

    // --- Demand brokerage, patient side ------------------------------------
    //
    // "dim" deliberately, despite being a question. It is not urgent — nobody is
    // blocked on the answer — and its real delivery is the email backstop, since
    // by construction this cohort has not opened the app in a fortnight. Badging
    // it would put a red dot on somebody who is not here to see it.
    "appointment-still-looking": {
        title: "Are you still looking?",
        tone: "dim",
        route: "/ava",
        family: "booking",
    },
    "appointment-booked": { title: "An appointment was booked", tone: "booked", route: "/appointment-requests", family: "booking" },
    "appointment-booked-patient": { title: "Your appointment is booked", tone: "booked", route: "/ava", family: "booking" },
    "appointment-declined": { title: "A provider passed on your request", tone: "dim", route: "/ava", family: "booking" },
    "appointment-offer-declined": { title: "A patient passed on your offer", tone: "dim", route: "/appointment-requests", family: "booking" },
    "appointment-offer-withdrawn": { title: "A provider withdrew an offer", tone: "dim", route: "/ava", family: "booking" },
    "appointment-request-withdrawn": { title: "A patient withdrew a request", tone: "dim", route: "/appointment-requests", family: "booking" },
    // --- Staff↔patient demand messaging (§8) --------------------------------
    "demand-message-patient": {
        // The patient always sees "Practice MD", never an individual staff
        // name — the title and toast keep that promise. Content never rides a
        // notification; the message itself is E2E-sealed.
        title: "Practice MD sent you a message",
        // action — a person on the team intervened in this patient's search; a
        // message nobody notices is a wasted intervention (it even escalates to
        // a content-free email at 1h server-side).
        tone: "action",
        route: "/ava",
        family: "message",
        toast: { title: "Practice MD sent you a message", body: "Read and reply in Ava" },
    },
    "demand-message-staff": {
        title: "The patient wrote to us",
        tone: "action",
        route: "/settings/appointment-demand",
        family: "message",
        toast: { title: "The patient wrote to us", body: "Reply from the demand console" },
    },
    "appointment-message-patient": {
        title: "A provider sent you a message",
        tone: "action",
        route: "/ava",
        family: "message",
        toast: { title: "New message", body: "A provider replied to you" },
    },
    "appointment-message-provider": {
        title: "A patient sent you a message",
        tone: "action",
        route: "/appointment-requests",
        family: "message",
        toast: { title: "New message", body: "A patient replied to you" },
    },
    "appointment-rate-prompt": { title: "Rate your visit", tone: "rate", route: "/ava", family: "booking" },
    // Paper-key attestation (ENG-81). Both open the attestation dialog IN
    // PLACE from the notification center (see PAPER_KEY_DIALOG_CATEGORIES in
    // NotificationCenter.tsx); the route is the fallback surface and the
    // toast-suppression surface. Deliberately NO email policy server-side:
    // "enter your 24 words" must never arrive by email — that trains the
    // exact reflex phishing needs. The ask lives behind device auth only.
    "create-paper-key": {
        title: "Create your Recovery Kit",
        tone: "action",
        route: "/settings/devices",
        family: "team",
        toast: { title: "Secure your account", body: "Your account has no paper key — create one now" },
    },
    "prove-paper-key": {
        title: "Confirm your paper key",
        tone: "action",
        route: "/settings/devices",
        family: "team",
        toast: { title: "Confirm your paper key", body: "It has been a while — confirm you still have it" },
    },
    "team-invite": {
        title: "You've been invited to a team",
        tone: "action",
        route: "/settings/teams",
        family: "team",
        toast: { title: "Team invitation", body: "You've been invited to a team" },
    },
    "team-join-request": {
        title: "Someone requested to join your team",
        tone: "action",
        route: "/settings/teams",
        family: "team",
        toast: { title: "Join request", body: "Someone wants to join your team" },
    },
    // --- Vetted provider-profile proposals ---------------------------------
    // Bodies are routing-only JSON, so every row renders its static title (the
    // team-family path fetches nothing). The two action categories settle by
    // ref server-side when the other side moves.
    "provider-profile-change-submitted": {
        // Addressed to the designated staff TEAM — its members review.
        title: "A profile change awaits review",
        tone: "action",
        route: "/settings/provider-profiles",
        family: "team",
        toast: { title: "A profile change awaits review", body: "Review it in Provider Profiles" },
    },
    "provider-profile-change-revise": {
        title: "Your profile change needs another pass",
        tone: "action",
        route: "/settings/provider-profiles",
        family: "team",
        toast: { title: "Your profile change needs another pass", body: "Read the note and revise" },
    },
    "provider-profile-change-vouched": {
        title: "Your profile change is live",
        tone: "dim",
        route: "/settings/provider-profiles",
        family: "team",
    },
    "provider-profile-change-superseded": {
        title: "Your profile change was replaced by a newer proposal",
        tone: "dim",
        route: "/settings/provider-profiles",
        family: "team",
    },
    "provider-profile-assigned": {
        title: "A provider profile was assigned to you",
        tone: "dim",
        route: "/settings/provider-profiles",
        family: "team",
    },
}

// The action-needed categories — drive the NEEDS YOU section and (in lockstep
// with the server's NeedsYouCategories) the unread badge. Pinned by categories.test.ts
// against a committed copy; the server pins the same set in TestNeedsYouCategoriesPinned.
export const ACTION_CATEGORIES = Object.keys(CATEGORIES).filter((c) => CATEGORIES[c].tone === "action")
